import { motion } from "framer-motion";
import homeImg from "./assets/home_ios.png";
import calendarImg from "./assets/calendar_ios.png";
import addReminderImg from "./assets/add_reminder_ios.png";
import logo from "./assets/logo.png";
import { useEffect, useState, useRef } from "react";
import appStoreLogo from "./assets/download_appstore.svg";

const isMobile = typeof window !== "undefined" && window.innerWidth < 768;


function App() {
  const [animateSides, setAnimateSides] = useState(false);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [showMotionDiv, setShowMotionDiv] = useState(!isMobile);


  useEffect(() => {
    if (isMobile) {
      const timer = setTimeout(() => {
        setShowMotionDiv(true);
      }, 1800);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimateSides(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Measure container width on mount and resize
  useEffect(() => {
    function updateWidth() {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    }
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  // Calculate animation distances as 40% of container width
  const animationDistance = containerWidth * 0.4; // 40%



  return (
    <div className="bg-rose-50 text-gray-800 font-mono min-h-screen overflow-hidden">
      <motion.header
        className="flex justify-between items-center px-2 pt-2 md:px-6 md:pt-6 max-w-7xl mx-auto"
        initial={{ opacity: 0, }}
        animate={{ opacity: 1, }}
        transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
      >
        <div className="flex items-center gap-2">
          <img src={logo} alt="Pillary Logo" className="w-12 h-12 object-contain" />
          <h1 className="text-2xl font-extrabold text-gray-800 ">Pillary</h1>
        </div>
        <nav className="hidden md:flex space-x-6 text-sm font-medium">
          {/* <a href="#features" className="hover:text-rose-500 transition">Features</a> */}
          {/* <a href="#download" className="hover:text-rose-500 transition">Download</a> */}
          {/* <a href="#contact" className="hover:text-rose-500 transition">Contact</a> */}
        </nav>
      </motion.header>

      <section
        className="flex flex-col-reverse md:flex-row items-center md:justify-between md:min-h-[calc(100vh-6rem)] max-w-7xl mx-auto px-6 py-10 md:py-0 gap-8 md:-translate-y-6"
      >
        {
          showMotionDiv && (
            <motion.div
              className="md:w-2/4 max-w-xl text-center md:text-left"
              variants={{
                hidden: {},
                show: {
                  transition: {
                    staggerChildren: 0.15, // delay between children
                  },
                },
              }}
              initial="hidden"
              animate="show"
            >
              <motion.h2
                className="text-3xl md:text-4xl font-extrabold leading-tight text-gray-800 mb-4"
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
                }}
              >
                No More Missed Doses. <br />Just Peace of Mind.
              </motion.h2>

              <motion.p
                className="text-base md:text-lg text-gray-700 mb-6"
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
                }}
              >
                Pillary helps you and your loved ones stay on top of daily medications with personalized reminders, a visual calendar, and complete privacy. All in a simple, elegant app.
              </motion.p>

              <motion.div
                className="flex flex-col sm:flex-row items-center justify-center md:justify-start gap-4"
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
                }}
              >
                <a
                  href="https://apps.apple.com/in/app/pillary-medicine-reminder/id6747710426"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center hover:scale-105 transition-transform"
                  data-umami-event="AppStore Button"
                >
                  <img src={appStoreLogo} alt="Download on the App Store" />
                </a>
                <button
                  disabled
                  className=" underline text-sm cursor-not-allowed p-0"
                  aria-disabled="true"
                  type="button"
                >
                  Android app coming soon
                </button>
              </motion.div>
            </motion.div>
          )
        }


        {/* Image stack container */}
        <div
          ref={containerRef} className="relative w-full max-w-[220px] md:max-w-[280px] lg:max-w-[320px] md:aspect-[9/16] md:mr-[120px] md:ml-10"
        >
          {/* Image B (Left) */}
          <motion.img
            src={addReminderImg}
            alt="Side Image Left"
            className="absolute top-1/2 left-0 rounded-3xl pointer-events-none select-none"
            style={{
              width: "100%",
              height: "auto",
              originX: 0.5,
              originY: 0.5,
              scale: 0.7,
              y: "-50%", // vertical center
              zIndex: 0,
            }}
            initial={{ x: 0, opacity: 0 }}
            animate={{ x: animateSides ? -animationDistance : 0, opacity: animateSides ? 1 : 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          />

          {/* Image C (Right) */}
          <motion.img
            src={calendarImg}
            alt="Side Image Right"
            className="absolute top-1/2 left-0 rounded-3xl pointer-events-none select-none"
            style={{
              width: "100%",
              height: "auto",
              originX: 0.5,
              originY: 0.5,
              scale: 0.7,
              y: "-50%",
              zIndex: 0,
            }}
            initial={{ x: 0, opacity: 0 }}
            animate={{ x: animateSides ? animationDistance : 0, opacity: animateSides ? 1 : 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          />

          {/* Image A (Center / Front) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.4 }}
            style={{ zIndex: 10 }}
          >
            <img
              src={homeImg}
              alt="Pillary App Screenshot"
              className="relative rounded-3xl w-full h-full object-contain"
            />
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default App;
